import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useMemo,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useAuth } from "../AuthContext";
import { useUserValues } from "../userprofile/UserContext";
import {
  PROBLEM_TYPE_ALL,
} from "../utils/problemType";
import {
  DIFFICULTY_ALL,
} from "../utils/difficulty";
import useLocalStorage from "../hooks/useLocalStorage";
import { queryKeys } from "../queryKeys";
export const CourseContext = createContext(null);

/* ────────────────────────
 *  Helpers
 * ──────────────────────── */
const createDefaultFilters = () => ({
  tags: [],
  points: 1,
  problemType: PROBLEM_TYPE_ALL,
});
const getCourseContent = async (selectedCourse, api, { isAdmin = false } = {}) => {
  const encodedCourse = encodeURIComponent(selectedCourse);
  const endpoints = isAdmin
    ? [
        `/kurs/course-content/${encodedCourse}`,
        `/kurs/manage/${encodedCourse}`,
        `/public/kurs/course-content/${encodedCourse}`,
      ]
    : [`/public/kurs/course-content/${encodedCourse}`];

  let lastError = null;
  for (const endpoint of endpoints) {
    try {
      const { data } = await api.get(endpoint, { params: { _ts: Date.now() } });
      return data;
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error("Failed to fetch course content");
};

const getCourseTextbooks = async (selectedCourse, api) => {
  const encodedCourse = encodeURIComponent(selectedCourse);
  const { data } = await api.get(`/public/kurs/textbooks/${encodedCourse}`, {
    params: { _ts: Date.now() },
  });
  return Array.isArray(data?.textbooks) ? data.textbooks : [];
};

const normalizeCourses = (data) => {
  const rows = Array.isArray(data)
    ? data
    : Array.isArray(data?.courses)
      ? data.courses
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data)
          ? data.data
          : [];

  return rows
    .map((course) => {
      if (typeof course === "string") {
        const name = course.trim();
        if (!name) return null;
        return { courseName: name, courseDisplayName: name };
      }

      const courseName = String(
        course?.courseName ?? course?.name ?? "",
      ).trim();
      if (!courseName) return null;

      const courseDisplayName = String(
        course?.courseDisplayName ??
          course?.label ??
          courseName,
      ).trim();

      return {
        courseName,
        courseDisplayName: courseDisplayName || courseName,
      };
    })
    .filter(Boolean);
};

const getAvailableCourses = async (api, { isAdmin = false } = {}) => {
  const endpoints = isAdmin ? ["/kurs/manage", "/public/kurs"] : ["/public/kurs"];

  let lastError = null;
  for (const endpoint of endpoints) {
    try {
      const { data } = await api.get(endpoint, { params: { _ts: Date.now() } });
      return normalizeCourses(data);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error("Failed to fetch courses");
};

// Robust normalization: accept 'name' or 'title', or raw strings
const normalizeSubtopics = (subList) =>
  (Array.isArray(subList) ? subList : [])
    .map((s) =>
      typeof s === "string" ? s : s?.name ?? s?.title ?? s?.label ?? null
    )
    .filter(Boolean);

const normalizeExampleProblems = (value) =>
  (Array.isArray(value) ? value : [])
    .map((problem) => {
      if (typeof problem === "string") {
        const problemText = problem.trim();
        if (!problemText) return null;
        return {
          description: "",
          problemText,
          answer: "",
          hint: "",
          solutionText: "",
        };
      }

      const description =
        typeof problem?.description === "string"
          ? problem.description.trim()
          : typeof problem?.title === "string"
            ? problem.title.trim()
            : "";
      const problemText =
        typeof problem?.problemText === "string"
          ? problem.problemText.trim()
          : typeof problem?.problem === "string"
            ? problem.problem.trim()
            : typeof problem?.text === "string"
              ? problem.text.trim()
              : "";
      const answer =
        typeof problem?.answer === "string" ? problem.answer.trim() : "";
      const hint =
        typeof problem?.hint === "string"
          ? problem.hint.trim()
          : typeof problem?.solutionHint === "string"
            ? problem.solutionHint.trim()
            : "";
      const solutionText =
        typeof problem?.solutionText === "string"
          ? problem.solutionText.trim()
          : typeof problem?.solution === "string"
            ? problem.solution.trim()
            : "";

      if (!description && !problemText && !answer && !hint && !solutionText) {
        return null;
      }

      return {
        description,
        problemText,
        answer,
        hint,
        solutionText,
      };
    })
    .filter(Boolean);

const exampleProblemToDescriptionBlock = (problem, index) => {
  const heading = problem.description || `Eksempeloppgave ${index}`;
  const lines = [];
  if (problem.problemText) {
    lines.push(`${heading}: ${problem.problemText}`);
  } else if (heading) {
    lines.push(heading);
  }
  if (problem.answer) lines.push(`Svar: ${problem.answer}`);
  if (problem.hint) lines.push(`Hint: ${problem.hint}`);
  if (problem.solutionText) lines.push(`Løsning: ${problem.solutionText}`);
  return lines.join("\n");
};

const composeDescription = (description, theory, exampleProblems) => {
  const sections = [];
  const normalizedDescription =
    typeof description === "string" ? description.trim() : String(description ?? "").trim();
  const normalizedTheory =
    typeof theory === "string" ? theory.trim() : String(theory ?? "").trim();
  if (normalizedDescription) sections.push(normalizedDescription);
  if (normalizedTheory) sections.push(normalizedTheory);
  const exampleBlocks = normalizeExampleProblems(exampleProblems)
    .map((problem, index) => exampleProblemToDescriptionBlock(problem, index + 1))
    .filter(Boolean);
  if (exampleBlocks.length) {
    sections.push(exampleBlocks.join("\n\n"));
  }
  return sections.join("\n\n");
};

// Accept multiple backend shapes and normalize to a single canonical list.
// Prefer the first non-empty candidate; otherwise use the longest.
export const extractTopicsFromAnyShape = (data) => {
  const candsRaw = [
    data?.courseContent?.topics,
    data?.course_content?.topics,
    data?.topics,
    data?.course?.courseContent?.topics,
    data?.course?.topics,
    data?.result?.courseContent?.topics,
    data?.data?.courseContent?.topics,
  ];

  const cands = candsRaw.filter(Array.isArray);
  if (cands.length === 0) return [];

  const nonEmpty = cands.filter((a) => a.length > 0);
  const arr = (nonEmpty.length ? nonEmpty : cands)
    .slice()
    .sort((a, b) => b.length - a.length)[0];

  return arr.map((t, tIdx) => {
    const rawSubtopics = t?.subtopics ?? t?.children ?? [];
    const subtopics = (Array.isArray(rawSubtopics) ? rawSubtopics : []).map(
      (s, sIdx) => ({
        number:
          typeof s === "string" ? sIdx + 1 : s?.number ?? sIdx + 1,
        name:
          typeof s === "string"
            ? s
            : s?.name ?? s?.title ?? s?.label ?? `Subtopic ${sIdx + 1}`,
        description:
          typeof s === "string"
            ? ""
            : composeDescription(
                typeof s?.description === "string"
                  ? s.description
                  : typeof s?.summary === "string"
                    ? s.summary
                    : "",
                s?.theory,
                s?.exampleProblems,
              ),
      }),
    );

    return {
      number: t?.number ?? tIdx + 1,
      topic: t?.topic ?? t?.title ?? t?.name ?? `Topic ${tIdx + 1}`,
      description:
        composeDescription(
          typeof t?.description === "string"
            ? t.description
            : typeof t?.summary === "string"
              ? t.summary
              : "",
          t?.theory,
          t?.exampleProblems,
        ),
      subtopics,
    };
  });
};

// Normalize a raw course-content response into everything consumers read.
const buildCourseContentModel = (raw) => {
  const normalizedTopics = extractTopicsFromAnyShape(raw);
  const subtopicsByTopic = {};
  for (const t of normalizedTopics) {
    const key = t.topic ?? t.title ?? t.name;
    if (!key) continue;
    subtopicsByTopic[key] = normalizeSubtopics(t.subtopics);
  }
  const rawInfo =
    raw?.courseInformation ??
    raw?.course?.courseInformation ??
    raw?.result?.courseInformation ??
    raw?.data?.courseInformation;
  return {
    courseContent: { ...(raw?.courseContent ?? {}), topics: normalizedTopics },
    topics: normalizedTopics,
    subtopicsByTopic,
    courseInformation:
      typeof rawInfo === "string" ? rawInfo.trim() : String(rawInfo ?? "").trim(),
  };
};

const GUEST_DEFAULT_COURSE = "1T";

// Stable fallbacks so effect/memo dependencies keep identity while queries
// have no data yet.
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

/* ────────────────────────
 *  Provider
 * ──────────────────────── */
export const CourseProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /* auth & user */
  const { activeCourse, isAdmin } = useUserValues();
  const {
    isAuthenticated,
    api,
    isLoading: authLoading,
    isLoggingOut,
  } = useAuth();

  /* routing helpers */
  const authPaths = ["/callback", "/logout", "/error"];
  const isAuthPath = authPaths.some((p) => location.pathname.startsWith(p));
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const courseFromURL = pathSegments[0] || null;

  /* available courses */
  const isAdminCourseList = isAdmin;

  const availableCoursesQuery = useQuery({
    queryKey: queryKeys.course.availableCourses(isAdminCourseList),
    queryFn: () => getAvailableCourses(api, { isAdmin: isAdminCourseList }),
    enabled: !authLoading && !isLoggingOut,
    // Fetched once per session/role; refreshAvailableCourses forces a reload.
    staleTime: Infinity,
    retry: false,
  });
  const availableCourses = availableCoursesQuery.data ?? EMPTY_ARRAY;
  const availableCoursesLoading = availableCoursesQuery.isLoading;
  const availableCoursesError = availableCoursesQuery.error ?? null;
  const refreshAvailableCourses = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.course.allAvailableCourses(),
      }),
    [queryClient],
  );

  const courseNames = useMemo(() => {
    return availableCourses
      .map((course) => course?.courseName)
      .filter(Boolean);
  }, [availableCourses]);

  const isValidCourse = useCallback(
    (course) => !!course && courseNames.includes(course),
    [courseNames],
  );

  /* main course selection */
  const initialSelectedCourse = activeCourse || courseFromURL || null;
  const persistCourseUiState = Boolean(isAuthenticated);

  // Keep latest chosen course across refresh/new tabs for all users.
  // 1T is just the first-time guest fallback when no course has been selected yet.
  const [selectedCourse, setSelectedCourse] = useLocalStorage(
    "selectedCourse",
    initialSelectedCourse,
    { persist: true },
  );

  /* topic (name + canonical number) */
  const [selectedTopic, setSelectedTopicRaw] = useLocalStorage(
    "selectedTopicName",
    "",
    { persist: persistCourseUiState },
  );
  const [topicNumber, setTopicNumber] = useLocalStorage("topicNumber", null, {
    persist: persistCourseUiState,
  });

  /* subtopic (name + canonical number) */
  const [selectedSubtopic, setSelectedSubtopicRaw] = useLocalStorage(
    "selectedSubtopicName",
    "",
    { persist: persistCourseUiState },
  );
  const [subtopicNumber, setSubtopicNumber] = useLocalStorage(
    "subtopicNumber",
    null,
    { persist: persistCourseUiState },
  );

  /* textbook navigation (sidebar built from the selected book's chapters) */
  const [selectedBookId, setSelectedBookIdRaw] = useLocalStorage(
    "selectedBookId",
    "",
    { persist: true },
  );
  const [selectedBookChapter, setSelectedBookChapter] = useLocalStorage(
    "selectedBookChapter",
    "",
    { persist: persistCourseUiState },
  );
  const [selectedBookSection, setSelectedBookSection] = useLocalStorage(
    "selectedBookSection",
    "",
    { persist: persistCourseUiState },
  );

  /* other UI state */
  const [selectedProblem, setSelectedProblem] = useLocalStorage(
    "selectedProblem",
    null,
    { persist: persistCourseUiState },
  );
  const [sortOption, setSortOption] = useLocalStorage("sortOption", null, {
    persist: persistCourseUiState,
  });
  const [filters, setFilters] = useState(createDefaultFilters);

  const difficultyFilter = DIFFICULTY_ALL;
  const setDifficultyFilter = useCallback(() => {}, []);

  const problemType = PROBLEM_TYPE_ALL;
  const setProblemType = useCallback(() => {}, []);

  /* misc */
  const isNavigating = useRef(false);
  const hasInitializedCourseRef = useRef(false);

  /* ── fetched course data (once per course; refreshCourseContent forces) ── */
  const courseContentEnabled =
    !isAuthPath && !authLoading && !isLoggingOut && !!selectedCourse;
  const courseContentQuery = useQuery({
    queryKey: queryKeys.course.content(selectedCourse, isAdminCourseList),
    queryFn: async () => {
      const raw = await getCourseContent(selectedCourse, api, {
        isAdmin: isAdminCourseList,
      });
      return buildCourseContentModel(raw);
    },
    enabled: courseContentEnabled,
    staleTime: Infinity,
    retry: false,
  });

  /* ── textbook structures for the selected course ──────────────────── */
  const textbooksQuery = useQuery({
    queryKey: queryKeys.course.textbooks(selectedCourse),
    queryFn: () => getCourseTextbooks(selectedCourse, api),
    enabled: courseContentEnabled,
    staleTime: Infinity,
    retry: false,
  });
  const textbooks = textbooksQuery.data ?? EMPTY_ARRAY;

  const selectedBook = useMemo(() => {
    const match = textbooks.find((book) => book.id === selectedBookId);
    // The canonical Reabel entry is the default mode, never a selected book.
    return match && !match.canonical ? match : null;
  }, [textbooks, selectedBookId]);

  // Graceful fallback: a stored book that is unknown, admin-disabled, or
  // the canonical entry itself silently reverts to the Reabel taxonomy.
  useEffect(() => {
    if (!selectedBookId || !textbooksQuery.isSuccess) return;
    const match = textbooks.find((book) => book.id === selectedBookId);
    if (!match || match.canonical) {
      setSelectedBookIdRaw("");
      setSelectedBookChapter("");
      setSelectedBookSection("");
    }
  }, [selectedBookId, textbooksQuery.isSuccess, textbooks]);

  const courseContent = courseContentQuery.data?.courseContent ?? EMPTY_OBJECT;
  const topics = courseContentQuery.data?.topics ?? EMPTY_ARRAY;
  const subtopicsByTopic =
    courseContentQuery.data?.subtopicsByTopic ?? EMPTY_OBJECT;
  const courseInformation = courseContentQuery.data?.courseInformation ?? "";
  const isLoading =
    authLoading ||
    courseContentQuery.isFetching ||
    (courseContentEnabled && courseContentQuery.isPending);
  const error = courseContentQuery.isError
    ? "Failed to fetch course content. Please try again later."
    : null;

  const refreshCourseContent = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.course.allContent(),
      }),
    [queryClient],
  );

  useEffect(() => {
    if (!selectedCourse) return;
    if (availableCoursesLoading) return;
    if (courseNames.length === 0) return;
    if (!isValidCourse(selectedCourse)) {
      setSelectedCourse(null);
    }
  }, [
    availableCoursesLoading,
    courseNames.length,
    isValidCourse,
    selectedCourse,
    setSelectedCourse,
  ]);

  useEffect(() => {
    if (isAuthPath || authLoading || isLoggingOut || isAuthenticated) return;
    if (selectedCourse) return;

    if (courseFromURL) {
      if (courseNames.length === 0 || isValidCourse(courseFromURL)) {
        setSelectedCourse(courseFromURL);
      }
      return;
    }

    if (courseNames.length === 0) {
      setSelectedCourse(GUEST_DEFAULT_COURSE);
      return;
    }

    const guestFallbackCourse = isValidCourse(GUEST_DEFAULT_COURSE)
      ? GUEST_DEFAULT_COURSE
      : (courseNames[0] || null);
    if (!guestFallbackCourse) return;
    setSelectedCourse(guestFallbackCourse);
  }, [
    authLoading,
    isAuthPath,
    isLoggingOut,
    isAuthenticated,
    courseFromURL,
    courseNames.length,
    isValidCourse,
    selectedCourse,
    setSelectedCourse,
    courseNames,
  ]);

  /* ── reset when course changes ────────────────────────────────────── */
  useEffect(() => {
    if (hasInitializedCourseRef.current) {
      setSelectedTopicRaw("");
      setTopicNumber(null);
      setSelectedSubtopicRaw("");
      setSubtopicNumber(null);
      setSelectedBookIdRaw("");
      setSelectedBookChapter("");
      setSelectedBookSection("");
    } else {
      hasInitializedCourseRef.current = true;
    }

    setSelectedProblem(null);
    setSortOption(null);
    setFilters(createDefaultFilters());
  }, [selectedCourse]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      window.localStorage.removeItem("difficultyFilter");
      window.localStorage.removeItem("problemType");
    } catch {
      // Ignore storage cleanup failures and keep runtime behavior intact.
    }
  }, []);

  /* ── sync selectedCourse with URL/activeCourse ────────────────────── */
  useEffect(() => {
    if (isAuthPath || authLoading || isLoggingOut) return;

    // 1) URL drives state (with activeCourse precedence)
    if (courseFromURL && isValidCourse(courseFromURL)) {
      if (
        activeCourse &&
        isValidCourse(activeCourse) &&
        activeCourse !== courseFromURL
      ) {
        setSelectedCourse(activeCourse);

        const newPathSegments = [...pathSegments];
        newPathSegments[0] = activeCourse;
        const newPath = `/${newPathSegments.join("/")}`;

        isNavigating.current = true;
        navigate(`${newPath}${location.search}${location.hash}`, {
          replace: true,
        });
        setTimeout(() => (isNavigating.current = false), 0);
      } else if (courseFromURL !== selectedCourse) {
        setSelectedCourse(courseFromURL);
      }
      return;
    }

    // 2) activeCourse updates selection without redirecting
    if (
      activeCourse &&
      isValidCourse(activeCourse) &&
      activeCourse !== selectedCourse
    ) {
      setSelectedCourse(activeCourse);
    }
  }, [
    activeCourse,
    authLoading,
    courseFromURL,
    isAuthPath,
    isLoggingOut,
    location.pathname,
    navigate,
    selectedCourse,
    setSelectedCourse,
    location.search,
    location.hash,
    pathSegments,
    isValidCourse,
  ]);

  /* ── handle navigation side-effects ───────────────────────────────── */
  useEffect(() => {
    if (isAuthPath || authLoading || isLoggingOut || isNavigating.current) return;

    if (courseFromURL) {
      if (isValidCourse(courseFromURL) && courseFromURL !== selectedCourse) {
        setSelectedCourse(courseFromURL);
      }
    }
  }, [
    authLoading,
    courseFromURL,
    isAuthPath,
    isLoggingOut,
    location.pathname,
    navigate,
    selectedCourse,
    setSelectedCourse,
    isValidCourse,
  ]);

  /* ── Derived: subtopics for current topic ─────────────────────────── */
  const subtopics = useMemo(
    () => (selectedTopic ? subtopicsByTopic[selectedTopic] || [] : []),
    [selectedTopic, subtopicsByTopic],
  );

  /* ── NUMBER SYNC ──────────────────────────────────────────────────── */
  useEffect(() => {
    if (!selectedTopic || topics.length === 0) {
      setTopicNumber(null);
      setSubtopicNumber(null);
      return;
    }
    const topicObj = topics.find((t) => t.topic === selectedTopic);
    setTopicNumber(topicObj ? topicObj.number : null);

    if (!selectedSubtopic || !topicObj) {
      setSubtopicNumber(null);
      return;
    }
    const st = (topicObj.subtopics || []).find((s) =>
      typeof s === "string"
        ? s === selectedSubtopic
        : (s?.name ?? s?.title) === selectedSubtopic,
    );
    setSubtopicNumber(typeof st === "string" ? null : st?.number ?? null);
  }, [topics, selectedTopic, selectedSubtopic]);

  /* ── wrapped setters (stable) ─────────────────────────────────────── */
  const selectTopic = useCallback(
    (topicName) => {
      if (topicName === selectedTopic) return; // avoid no-op re-renders
      setSelectedTopicRaw(topicName);
      const t = topics.find((t) => t.topic === topicName);
      setTopicNumber(t ? t.number : null);
      setSelectedSubtopicRaw("");
      setSubtopicNumber(null);
    },
    [topics, selectedTopic],
  );

  const selectSubtopic = useCallback(
    (subtopicName, parentTopic = selectedTopic) => {
      if (!parentTopic) return;
      if (parentTopic !== selectedTopic) {
        setSelectedTopicRaw(parentTopic);
        const t = topics.find((t) => t.topic === parentTopic);
        setTopicNumber(t ? t.number : null);
      }
      setSelectedSubtopicRaw(subtopicName ?? "");
    },
    [topics, selectedTopic],
  );

  const selectBook = useCallback(
    (bookId) => {
      setSelectedBookIdRaw(bookId || "");
      setSelectedBookChapter("");
      setSelectedBookSection("");
    },
    [setSelectedBookIdRaw, setSelectedBookChapter, setSelectedBookSection],
  );

  const selectBookSection = useCallback(
    (chapterNumber, sectionNumber) => {
      setSelectedBookChapter(chapterNumber || "");
      setSelectedBookSection(sectionNumber || "");
    },
    [setSelectedBookChapter, setSelectedBookSection],
  );

  const setFilter = useCallback(
    (key, val) => setFilters((prev) => ({ ...prev, [key]: val })),
    [],
  );

  const toggleProblemType = useCallback(
    () => {},
    [],
  );

  const resetCourseFilters = useCallback(() => {
    setSelectedTopicRaw("");
    setTopicNumber(null);
    setSelectedSubtopicRaw("");
    setSubtopicNumber(null);
    setFilters(createDefaultFilters());
  }, [
    setSelectedTopicRaw,
    setTopicNumber,
    setSelectedSubtopicRaw,
    setSubtopicNumber,
    setFilters,
  ]);

  /* ── exposed context value (stable refs) ──────────────────────────── */
  const value = useMemo(
    () => ({
      selectedCourse,
      setSelectedCourse,

      availableCourses,
      availableCoursesLoading,
      availableCoursesError,
      courseNames,

      selectedTopic,
      setSelectedTopic: selectTopic,
      topicNumber,

      selectedSubtopic,
      setSelectedSubtopic: selectSubtopic,
      subtopicNumber,

      topics,
      subtopicsByTopic,
      subtopics,

      textbooks,
      textbooksLoaded: textbooksQuery.isSuccess,
      textbooksFailed: textbooksQuery.isError,
      selectedBookId,
      selectedBook,
      setSelectedBook: selectBook,
      selectedBookChapter,
      selectedBookSection,
      selectBookSection,

      courseContent,
      courseInformation,
      isLoading,
      error,

      selectedProblem,
      setSelectedProblem,

      sortOption,
      setSortOption,
      difficultyFilter,
      setDifficultyFilter,

      filters,
      setFilters,
      setFilter,

      problemType,
      setProblemType,
      toggleProblemType,

      resetCourseFilters,

      refreshCourseContent,   // ← expose refresher for manager/sidebar
      refreshAvailableCourses,
    }),
    [
      selectedCourse,
      availableCourses,
      availableCoursesLoading,
      availableCoursesError,
      courseNames,
      selectedTopic,
      topicNumber,
      selectedSubtopic,
      subtopicNumber,
      topics,
      subtopicsByTopic,
      subtopics,
      textbooks,
      textbooksQuery.isSuccess,
      textbooksQuery.isError,
      selectedBookId,
      selectedBook,
      selectBook,
      selectedBookChapter,
      selectedBookSection,
      selectBookSection,
      courseContent,
      courseInformation,
      isLoading,
      error,
      selectedProblem,
      sortOption,
      difficultyFilter,
      filters,
      setSortOption,
      setDifficultyFilter,
      problemType,
      setProblemType,
      toggleProblemType,
      resetCourseFilters,
      refreshCourseContent,
      refreshAvailableCourses,
    ],
  );

  return <CourseContext.Provider value={value}>{children}</CourseContext.Provider>;
};

CourseProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useCourseValues = () => {
  const ctx = useContext(CourseContext);
  if (!ctx) throw new Error("useCourseValues must be used within CourseProvider");
  return ctx;
};
